import { Container, Divider, Grid, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextField from '../../components/Form/TextField';
import ButtonUI from '../../components/ui/ButtonUI';
import LoadingUI from '../../components/ui/LoadingUI';
import PageTitle from '../../components/ui/PageTitleUI';
import VehicleApi from '../../services/VehicleApi';
import VehicleTypeApi from '../../services/VehicleTypeApi';
import { useStyles } from './Style';

interface IFormInput {
    vehiclePlate: string;
}

const RemoveCache: React.FC = () => {
    const classes = useStyles();
    const { t } = useTranslation('RemoveCache');
    const { enqueueSnackbar } = useSnackbar();
    const [loadingVehicle, setLoadingVehicle] = useState(false);
    const [loadingVehicleType, setLoadingVehicleType] = useState(false);

    const { control, getValues, reset } = useForm<IFormInput>({
        defaultValues: { vehiclePlate: '' },
    });

    const handleRemoveVehicleCache = async () => {
        const { vehiclePlate } = getValues();
        setLoadingVehicle(true);
        try {
            await VehicleApi.deleteVehicleCache(vehiclePlate?.trim() || undefined);
            enqueueSnackbar(t('vehicleCacheSuccess'), { variant: 'success' });
            reset({ vehiclePlate: '' });
        } catch {
            enqueueSnackbar(t('vehicleCacheError'), { variant: 'error' });
        } finally {
            setLoadingVehicle(false);
        }
    };

    const handleRemoveVehicleTypeCache = async () => {
        setLoadingVehicleType(true);
        try {
            await VehicleTypeApi.deleteVehicleTypeCache();
            enqueueSnackbar(t('vehicleTypeCacheSuccess'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('vehicleTypeCacheError'), { variant: 'error' });
        } finally {
            setLoadingVehicleType(false);
        }
    };

    return (
        <Container className={classes.container}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <PageTitle title={t('pageTitle')} />
                </Grid>

                {/* ── Vehicle Cache ─────────────────────────────────────────── */}
                <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                        {t('vehicleCacheTitle')}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <TextField
                        name="vehiclePlate"
                        control={control}
                        label={t('vehiclePlate')}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={8} style={{ display: 'flex', alignItems: 'center' }}>
                    {loadingVehicle ? (
                        <LoadingUI />
                    ) : (
                        <ButtonUI
                            color="primary"
                            onClick={handleRemoveVehicleCache}
                            aria-label={t('removeVehicleCache')}
                        >
                            {t('removeVehicleCache')}
                        </ButtonUI>
                    )}
                </Grid>

                <Grid item xs={12}>
                    <Divider className={classes.divider} />
                </Grid>

                {/* ── VehicleType Cache ─────────────────────────────────────── */}
                <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                        {t('vehicleTypeCacheTitle')}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    {loadingVehicleType ? (
                        <LoadingUI />
                    ) : (
                        <ButtonUI
                            color="primary"
                            onClick={handleRemoveVehicleTypeCache}
                            aria-label={t('removeVehicleTypeCache')}
                        >
                            {t('removeVehicleTypeCache')}
                        </ButtonUI>
                    )}
                </Grid>
            </Grid>
        </Container>
    );
};

export default RemoveCache;
